<script setup>
import { i18n } from 'boot/i18n';
import { HrInfoBanner, HrSupportingBtn } from 'src/design-system';
import { KNOWLEDGE_BASE_ARTICLES } from 'src/constants/knowledgeBase';
import { careModelAnalytics } from 'src/services/analytics';
import { auth as useAuthStore } from 'src/store/auth';
import { userRoles } from 'src/utils/AppUtils';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { LAUNCH_BANNER_VARIANTS, resolveLaunchBannerVariant } from '../../domain';
import { hasClinicEverAudited } from '../../infrastructure/api/auditUsageApi';

const SUBSCRIPTION_ROUTE = '/settings/subscription';
const DISMISSED_KEY = 'noteAuditorLaunchBannerDismissed';
const VIEW_REPORTED_KEY = 'noteAuditorLaunchBannerViewReported';

const authStore = useAuthStore();
const router = useRouter();

const hasEverAudited = ref(null);
const isDismissed = ref(false);

const userId = computed(() => authStore.userDetails?.userId ?? null);
const isManager = computed(() => authStore.userDetails?.role === userRoles.MANAGER);

const storageKey = (prefix) => `${prefix}:${userId.value}`;

const readFlag = (storageName, prefix) => {
  try {
    return window[storageName].getItem(storageKey(prefix)) === 'true';
  } catch {
    return false;
  }
};

const writeFlag = (storageName, prefix) => {
  try {
    window[storageName].setItem(storageKey(prefix), 'true');
  } catch {
    // A viewer who cannot persist the dismissal sees it again next visit, which is acceptable.
  }
};

const variant = computed(() => resolveLaunchBannerVariant({
  isManager: isManager.value,
  isDismissed: isDismissed.value,
  hasEverAudited: hasEverAudited.value,
}));

const heading = computed(() => (
  variant.value === LAUNCH_BANNER_VARIANTS.RETURNING
    ? i18n.t('noteAuditor.launchBanner.headingReturning')
    : i18n.t('noteAuditor.launchBanner.headingNew')
));

const body = computed(() => (
  variant.value === LAUNCH_BANNER_VARIANTS.RETURNING
    ? i18n.t('noteAuditor.launchBanner.bodyReturning')
    : i18n.t('noteAuditor.launchBanner.bodyNew')
));

function reportView() {
  if (readFlag('sessionStorage', VIEW_REPORTED_KEY)) {
    return;
  }

  careModelAnalytics.trackAuditLaunchBannerViewed(variant.value);
  writeFlag('sessionStorage', VIEW_REPORTED_KEY);
}

function onHowItWorks() {
  careModelAnalytics.trackAuditLaunchBannerCtaClicked(variant.value, 'howItWorks');
  window.open(KNOWLEDGE_BASE_ARTICLES.NOTE_AUDITOR, '_blank', 'noopener');
}

function onTurnItOn() {
  careModelAnalytics.trackAuditLaunchBannerCtaClicked(variant.value, 'turnItOn');
  router.push(SUBSCRIPTION_ROUTE);
}

function onDismiss() {
  careModelAnalytics.trackAuditLaunchBannerDismissed(variant.value);
  writeFlag('localStorage', DISMISSED_KEY);
  isDismissed.value = true;
}

onMounted(async () => {
  if (!isManager.value) {
    return;
  }

  isDismissed.value = readFlag('localStorage', DISMISSED_KEY);

  if (isDismissed.value) {
    return;
  }

  try {
    hasEverAudited.value = await hasClinicEverAudited(authStore.userDetails?.clinic);
  } catch {
    return;
  }

  reportView();
});
</script>

<template>
  <hr-info-banner
    v-if="variant"
    full-bleed
    dismissible
    data-e2e="banner-note-auditor-launch"
    :heading="heading"
    :message="body"
    :close-label="i18n.t('noteAuditor.launchBanner.closeLabel')"
    class="q-mb-md"
    @dismiss="onDismiss"
  >
    <template #action>
      <hr-supporting-btn
        data-e2e="button-note-auditor-launch-how-it-works"
        :label="i18n.t('noteAuditor.launchBanner.buttonHowItWorks')"
        :legacy-props="{ noCaps: true }"
        @click="onHowItWorks"
      />
      <hr-supporting-btn
        data-e2e="button-note-auditor-launch-turn-it-on"
        :label="i18n.t('noteAuditor.launchBanner.buttonTurnItOn')"
        :legacy-props="{ noCaps: true }"
        @click="onTurnItOn"
      />
    </template>
  </hr-info-banner>
</template>
